import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Logo from "./Logo";
import LanguageSelector from "./LanguageSelector";
import MobileMenu, { NAV_LINKS, isCurrent } from "./MobileMenu";
import "../styles/header.css";

const MENU_ID = "site-menu";

/**
 * Sticky translucent header, 64 px (spec c.7 §1). >= 1024: logo, four links,
 * language control, primary CTA. < 1024: logo, short CTA, menu toggle (sheet).
 * "Sign in" lives in the footer only. No scroll listener.
 */
const Header: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) toggleRef.current?.focus();
  }, []);

  return (
    <header className="site-header" ref={headerRef} data-menu-open={open ? "true" : undefined}>
      <div className="container header-inner">
        <Link className="header-logo" to="/" aria-label={t("header.homeAriaLabel")}>
          <Logo />
        </Link>

        <div className="header-right">
          <nav className="header-nav" aria-label={t("header.navAriaLabel")}>
            <ul className="nav-list">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    className="nav-link"
                    href={l.href}
                    aria-current={isCurrent(l.href) ? "page" : undefined}
                  >
                    {t(l.key)}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <LanguageSelector className="header-lang" />

          <a className="btn btn--primary header-cta" href="/#contact">
            <span className="header-cta-long">{t("header.cta")}</span>
            <span className="header-cta-short">{t("header.ctaShort")}</span>
          </a>

          <button
            ref={toggleRef}
            type="button"
            className="menu-btn"
            aria-expanded={open}
            aria-controls={MENU_ID}
            onClick={() => (open ? close(false) : setOpen(true))}
          >
            <span className="menu-btn-bar" aria-hidden="true" />
            <span className="menu-btn-bar" aria-hidden="true" />
            <span className="visually-hidden">{t("header.menu")}</span>
          </button>
        </div>
      </div>

      <MobileMenu id={MENU_ID} open={open} onClose={close} trapRoot={headerRef} />
    </header>
  );
};

export default Header;
